import React, { useEffect, useRef, useState } from 'react';
import {
    Drawer, Box, Stack, Typography, Avatar, IconButton, Button, Menu, MenuItem,
    TextField, ImageList, ImageListItem, CircularProgress, Tooltip, Badge,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import toast from 'react-hot-toast';
import http, { apiErrorMessage } from '../../Api/http';
import { GetGroupInfo, UpdateGroup, MuteGroup, UpdateMemberRole, FetchMedia } from '../../Api/Api';
import { solidBtn, ghostBtn, dangerBtn, tonalBtn, ConfirmDialog } from '../uiKit';
import { PRIMARY, PRIMARY_BORDER } from '../../theme';
import { avatarToneFor, initialsFrom, classifyFile, parseSentOn, extractMediaList } from './chatHelpers';
import { onChatPresence, onChatReconnected } from './chatSocket';

const MUTE_OPTIONS = [
    { label: '8 hours', hours: 8 },
    { label: '1 week', hours: 24 * 7 },
    { label: 'Always', hours: -1 },
    { label: 'Unmute', hours: 0 },
];

export default function GroupInfoPanel({
    open, onClose, group, myUserId, canManage, canDelete,
    onManageMembers, onLeave, onDelete, onClearChat, onGroupChanged,
}) {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [iconFile, setIconFile] = useState(null);
    const [iconPreview, setIconPreview] = useState('');
    const [removeIcon, setRemoveIcon] = useState(false);
    const iconRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [muteAnchor, setMuteAnchor] = useState(null);
    const [media, setMedia] = useState([]);
    const [loadingMedia, setLoadingMedia] = useState(false);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmClear, setConfirmClear] = useState(false);
    // Live online/offline deltas observed *while this panel is mounted*,
    // layered on top of the REST snapshot in `info.members[].isOnline`
    // (GetGroupInfo's own field is already "live" at fetch time — see
    // chatSocket.js's onChatPresence doc — this just keeps it live after
    // that point too, e.g. someone comes online while the panel stays open).
    const [presenceOverrides, setPresenceOverrides] = useState({});

    const load = React.useCallback(() => {
        if (!group?.groupId) return;
        setLoading(true);
        http.get(GetGroupInfo, { params: { groupId: group.groupId } })
            .then(({ data: body }) => {
                if (body?.error) throw new Error(body.message);
                setInfo(body?.data || null);
                // A fresh snapshot supersedes any deltas gathered against
                // the previous one.
                setPresenceOverrides({});
            })
            .catch(() => setInfo(null))
            .finally(() => setLoading(false));
    }, [group?.groupId]);

    useEffect(() => {
        const unsub = onChatPresence((userId, online) => {
            setPresenceOverrides((m) => ({ ...m, [userId]: online }));
        });
        return unsub;
    }, []);

    // The hub connection can be torn down and rebuilt while this panel stays
    // open (tab blur/focus — see chatSocket.js's module comment), which
    // resets the global presence registry. Re-fetching here keeps member
    // dots accurate instead of frozen at whatever they were before the drop.
    useEffect(() => {
        if (!open) return undefined;
        return onChatReconnected(load);
    }, [open, load]);

    useEffect(() => {
        if (!open || !group?.groupId) return;
        load();
        setName(group.name || '');
        setDescription(group.description || '');
        setIconFile(null); setIconPreview(''); setRemoveIcon(false); setEditing(false);
        setLoadingMedia(true);
        http.get(FetchMedia, { params: { groupId: group.groupId, pageSize: 30 } })
            .then(({ data: body }) => setMedia(extractMediaList(body)))
            .catch(() => setMedia([]))
            .finally(() => setLoadingMedia(false));
        // Only reset name/description from the group when the panel opens for
        // a (possibly different) group — not on every `group` prop change —
        // so it doesn't clobber in-progress edits when a live GroupUpdated
        // refresh comes in while the user is typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, group?.groupId, load]);

    const members = Array.isArray(info?.members) ? info.members : [];

    // GetGroupInfo's exact field names for "who created this / when" aren't
    // spelled out verbatim in the integration doc, so this reads a handful of
    // likely variants defensively (mirrors chatHelpers' own normalizeGroup
    // tolerance) and simply omits the section below when none are present,
    // rather than inventing a field the backend may not return.
    const creatorName = info?.createdByName ?? info?.creatorName ?? info?.createdBy
        ?? members.find((m) => m.isCreator)?.name
        ?? (group?.creatorUserId != null ? members.find((m) => m.userId === group.creatorUserId)?.name : null)
        ?? null;
    const createdOnRaw = info?.createdOn ?? info?.createdOnDisplay ?? group?.createdOn ?? null;
    const createdOnParsed = createdOnRaw ? parseSentOn(createdOnRaw) : null;
    const createdOnDisplay = createdOnParsed
        ? createdOnParsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : (typeof createdOnRaw === 'string' ? createdOnRaw : null);

    const pickIcon = (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setIconFile(f);
        setIconPreview(URL.createObjectURL(f));
        setRemoveIcon(false);
    };

    const clearIcon = (e) => {
        e.stopPropagation();
        setIconFile(null);
        setIconPreview('');
        setRemoveIcon(true);
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('GroupId', group.groupId);
            if (name.trim() && name.trim() !== group.name) fd.append('Name', name.trim());
            if (description !== group.description) fd.append('Description', description || '');
            if (iconFile) fd.append('IconFile', iconFile);
            // RemoveIcon (doc §4.1) clears the icon without replacing it —
            // only meaningful when the user didn't also pick a new one.
            if (removeIcon && !iconFile) fd.append('RemoveIcon', 'true');
            const { data: body } = await http.put(UpdateGroup, fd);
            if (body?.error) throw new Error(body.message || 'Could not update the group.');
            toast.success('Group updated.');
            setEditing(false);
            setRemoveIcon(false);
            onGroupChanged?.();
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not update the group.'));
        } finally {
            setSaving(false);
        }
    };

    const mute = async (hours) => {
        setMuteAnchor(null);
        try {
            const { data: body } = await http.put(MuteGroup, { GroupId: group.groupId, MuteHours: hours });
            if (body?.error) throw new Error(body.message);
            toast.success(hours === 0 ? 'Unmuted.' : 'Notifications muted.');
            onGroupChanged?.();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not update mute settings.'));
        }
    };

    const changeRole = async (targetUserId, role) => {
        try {
            const { data: body } = await http.put(UpdateMemberRole, { GroupId: group.groupId, TargetUserId: targetUserId, Role: role });
            if (body?.error) throw new Error(body.message);
            toast.success(role === 'admin' ? 'Promoted to group admin.' : 'Demoted to member.');
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Could not change member role.'));
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 380 } } } }}>
            <Stack direction="row" sx={{ alignItems: 'center', px: 2, py: 1.6, borderBottom: '1px solid #EEF0F6' }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0F172A', flex: 1 }}>Group info</Typography>
                <IconButton size="small" onClick={onClose}><CloseRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
            </Stack>

            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={22} /></Box>
                ) : (
                    <>
                        <Box sx={{ p: 2.4, textAlign: 'center', borderBottom: '1px solid #EEF0F6' }}>
                            <input ref={iconRef} type="file" hidden accept="image/*" onChange={pickIcon} />
                            <Box sx={{ position: 'relative', width: 74, height: 74, mx: 'auto', mb: 1.2 }}>
                                <Box
                                    onClick={() => editing && iconRef.current?.click()}
                                    sx={{
                                        width: 74, height: 74, borderRadius: '16px', cursor: editing ? 'pointer' : 'default',
                                        bgcolor: avatarToneFor(group?.name).bgcolor, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        backgroundImage: !removeIcon && (iconPreview || group?.iconUrl) ? `url(${iconPreview || group.iconUrl})` : undefined,
                                        backgroundSize: 'cover', backgroundPosition: 'center', border: editing ? `1px dashed ${PRIMARY_BORDER}` : 'none',
                                    }}
                                >
                                    {(removeIcon || (!iconPreview && !group?.iconUrl)) && (
                                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: avatarToneFor(group?.name).color }}>{initialsFrom(group?.name)}</Typography>
                                    )}
                                </Box>
                                {editing && !removeIcon && (iconPreview || group?.iconUrl) && (
                                    <Tooltip arrow title="Remove icon">
                                        <IconButton
                                            size="small"
                                            onClick={clearIcon}
                                            sx={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, bgcolor: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,0.2)', '&:hover': { bgcolor: '#E4F0F9' } }}
                                        >
                                            <CloseRoundedIcon sx={{ fontSize: 13 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            {editing ? (
                                <Stack spacing={1} sx={{ px: 1 }}>
                                    <TextField size="small" value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name" />
                                    <TextField size="small" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" multiline minRows={2} />
                                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
                                        <Button onClick={() => { setEditing(false); setIconFile(null); setIconPreview(''); setRemoveIcon(false); setName(group?.name || ''); setDescription(group?.description || ''); }} sx={{ ...ghostBtn, height: 34, px: 1.6, fontSize: 12.5 }}>Cancel</Button>
                                        <Button onClick={saveEdit} disabled={saving} sx={{ ...solidBtn, height: 34, px: 1.6, fontSize: 12.5 }}>{saving ? 'Saving…' : 'Save'}</Button>
                                    </Stack>
                                </Stack>
                            ) : (
                                <>
                                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }}>{group?.name}</Typography>
                                        {canManage && (
                                            <IconButton size="small" onClick={() => setEditing(true)}><EditRoundedIcon sx={{ fontSize: 15, color: '#94A3B8' }} /></IconButton>
                                        )}
                                    </Stack>
                                    {group?.description && <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.4, px: 1 }}>{group.description}</Typography>}
                                    <Typography sx={{ fontSize: 11, color: '#98A0AE', mt: 0.6 }}>{members.length} member{members.length === 1 ? '' : 's'}</Typography>
                                </>
                            )}
                        </Box>

                        {creatorName && (
                            <Box sx={{ px: 1.8, py: 1.1, borderBottom: '1px solid #EEF0F6' }}>
                                <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>
                                    Created by <Box component="span" sx={{ fontWeight: 700, color: '#334155' }}>{creatorName}</Box>
                                    {createdOnDisplay ? ` · ${createdOnDisplay}` : ''}
                                </Typography>
                            </Box>
                        )}

                        <Stack direction="row" spacing={1} sx={{ p: 1.6, borderBottom: '1px solid #EEF0F6', flexWrap: 'wrap', gap: 1 }}>
                            {canManage && (
                                <Button startIcon={<PersonAddRoundedIcon sx={{ fontSize: 16 }} />} onClick={onManageMembers} sx={{ ...tonalBtn, height: 34, fontSize: 12, px: 1.4 }}>Add / remove</Button>
                            )}
                            <Button
                                startIcon={group?.isMuted ? <VolumeUpRoundedIcon sx={{ fontSize: 16 }} /> : <VolumeOffRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={(e) => setMuteAnchor(e.currentTarget)}
                                sx={{ ...ghostBtn, height: 34, fontSize: 12, px: 1.4 }}
                            >
                                {group?.isMuted ? 'Muted' : 'Mute'}
                            </Button>
                            <Menu anchorEl={muteAnchor} open={Boolean(muteAnchor)} onClose={() => setMuteAnchor(null)}>
                                {MUTE_OPTIONS.map((o) => <MenuItem key={o.label} onClick={() => mute(o.hours)}>{o.label}</MenuItem>)}
                            </Menu>
                            <Button startIcon={<CleaningServicesRoundedIcon sx={{ fontSize: 16 }} />} onClick={() => setConfirmClear(true)} sx={{ ...ghostBtn, height: 34, fontSize: 12, px: 1.4 }}>Clear chat</Button>
                        </Stack>

                        <Box sx={{ p: 1.8, borderBottom: '1px solid #EEF0F6' }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Members</Typography>
                            <Stack spacing={0.4}>
                                {members.map((m) => {
                                    const tone = avatarToneFor(m.name);
                                    const isMe = m.userId === myUserId;
                                    const online = presenceOverrides[m.userId] ?? m.isOnline;
                                    return (
                                        <Stack key={m.userId} direction="row" spacing={1.2} sx={{ alignItems: 'center', py: 0.7 }}>
                                            <Badge
                                                overlap="circular"
                                                variant="dot"
                                                sx={{ '& .MuiBadge-badge': { bgcolor: online ? '#22C55E' : '#CBD2DD', border: '2px solid #fff' } }}
                                            >
                                                <Avatar sx={{ width: 32, height: 32, ...tone, fontSize: 11.5, fontWeight: 800 }}>{initialsFrom(m.name)}</Avatar>
                                            </Badge>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A' }} noWrap>{m.name}{isMe ? ' (you)' : ''}</Typography>
                                                {m.designation && <Typography sx={{ fontSize: 10.5, color: '#98A0AE' }} noWrap>{m.designation}</Typography>}
                                            </Box>
                                            {(m.role === 'admin' || m.isCreator) && (
                                                <Tooltip arrow title={m.isCreator ? 'Group creator' : 'Group admin'}>
                                                    <AdminPanelSettingsRoundedIcon sx={{ fontSize: 16, color: PRIMARY }} />
                                                </Tooltip>
                                            )}
                                            {canManage && !m.isCreator && !isMe && (
                                                <Button
                                                    size="small"
                                                    onClick={() => changeRole(m.userId, m.role === 'admin' ? 'member' : 'admin')}
                                                    sx={{ textTransform: 'none', fontSize: 10.5, fontWeight: 700, color: PRIMARY, minWidth: 0, px: 0.8 }}
                                                >
                                                    {m.role === 'admin' ? 'Demote' : 'Make admin'}
                                                </Button>
                                            )}
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>

                        <Box sx={{ p: 1.8, borderBottom: '1px solid #EEF0F6' }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Shared media</Typography>
                            {loadingMedia ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={18} /></Box>
                            ) : media.length === 0 ? (
                                <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>No media shared yet.</Typography>
                            ) : (
                                <ImageList cols={3} gap={6} sx={{ m: 0 }}>
                                    {media.map((m, i) => {
                                        const kind = classifyFile(m.attachmentName || m.attachmentUrl);
                                        return (
                                            <ImageListItem key={m.messageId || i} sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                                                {kind.category === 'image' ? (
                                                    <Box component="a" href={m.attachmentUrl} target="_blank" rel="noopener noreferrer">
                                                        <Box component="img" src={m.attachmentUrl} alt="" sx={{ width: '100%', height: 74, objectFit: 'cover', display: 'block' }} />
                                                    </Box>
                                                ) : (
                                                    <Box component="a" href={m.attachmentUrl} target="_blank" rel="noopener noreferrer" sx={{ width: '100%', height: 74, bgcolor: '#E4F0F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <InsertDriveFileRoundedIcon sx={{ fontSize: 22, color: PRIMARY }} />
                                                    </Box>
                                                )}
                                            </ImageListItem>
                                        );
                                    })}
                                </ImageList>
                            )}
                        </Box>

                        <Stack spacing={1} sx={{ p: 1.8 }}>
                            {!group?.isCreator && (
                                <Button startIcon={<LogoutRoundedIcon sx={{ fontSize: 16 }} />} onClick={() => setConfirmLeave(true)} sx={{ ...dangerBtn, height: 38, fontSize: 12.5 }}>Leave group</Button>
                            )}
                            {canDelete && (
                                <Button startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />} onClick={() => setConfirmDelete(true)} sx={{ ...dangerBtn, height: 38, fontSize: 12.5 }}>Delete group</Button>
                            )}
                        </Stack>
                    </>
                )}
            </Box>

            <ConfirmDialog open={confirmLeave} onClose={() => setConfirmLeave(false)} onConfirm={() => { setConfirmLeave(false); onLeave(); }} title="Leave this group?" body="You'll stop receiving new messages. You can be re-added later." confirmLabel="Leave" />
            <ConfirmDialog open={confirmDelete} onClose={() => setConfirmDelete(false)} onConfirm={() => { setConfirmDelete(false); onDelete(); }} title="Delete this group?" body="This removes the group for everyone. History stays server-side but the group becomes unusable." confirmLabel="Delete" />
            <ConfirmDialog open={confirmClear} onClose={() => setConfirmClear(false)} onConfirm={() => { setConfirmClear(false); onClearChat(); }} title="Clear this chat?" body="This clears the chat only on your account — nothing changes for other members." confirmLabel="Clear" />
        </Drawer>
    );
}
